<template>
  <div class="w-100">
    <AcknowledgementSettingsRevertModal
      :show="showRevertModal"
      :field-name="pendingFieldLabel"
      :default-value="pendingRevertDefault"
      @close="handleRevertModalClose"
      @confirm="handleRevertConfirm"
    />

    <div
      v-if="pageLoading"
      class="form-wrapper"
    >
      <v-skeleton-loader
        type="text"
        width="220"
        height="24"
        class="mb-4"
      />
      <v-skeleton-loader
        type="text"
        width="100%"
        height="48"
        class="mb-4"
      />
      <div class="d-flex align-center ga-2 mb-4">
        <v-skeleton-loader
          type="text"
          width="75"
          height="40"
        />
        <v-skeleton-loader
          type="text"
          width="36"
          height="20"
        />
      </div>
      <v-skeleton-loader
        type="button"
        width="120"
        height="40"
      />
    </div>

    <div
      v-else
      class="form-wrapper"
    >
      <v-form class="flsa-form">
        <div>
          <h2 class="text-h1 text-textDarkest mb-0 w-100">
            FLSA Survey Timelines
          </h2>
          <p class="section-description mb-0">
            Update this timeline to receive alerts when FLSA surveys are overdue for action.
            <a
              href="#"
              class="section-description-link"
              data-beacon-article-sidebar="6a1087c8195a6651cbcc5deb"
              @click.stop
            >
              Learn more about FLSA survey timelines.
            </a>
          </p>
        </div>

        <div class="two-col-grid">
          <div class="field-block">
            <h3 class="text-h2 text-textDarkest mb-0">
              FLSA Survey Cycle SLA
            </h3>
            <p class="section-desc">
              How often your company requires FLSA surveys on job profiles to be updated.
              You'll be alerted via the
              <router-link
                class="section-description-link"
                :to="{ name: 'federal-flsa-dashboard' }"
                target="_blank"
              >
                FLSA Survey Report
              </router-link>
              when a job profile is overdue for an updated FLSA survey. For an annual requirement,
              enter 365.
            </p>
            <div class="d-flex align-center ga-2 form-field">
              <DpplFormField
                field-name="flsa_survey_cycle_sla"
                :form="form"
                v-bind="$attrs"
                @update:model-value="handleFormValueUpdate($event)"
              >
                <template #field="{ props, on }">
                  <DpplTextField
                    v-bind="props"
                    class="input-numeric"
                    :class="{ 'input-is-default': isFieldDefault('flsa_survey_cycle_sla') }"
                    :placeholder="defaults.flsa_survey_cycle_sla"
                    label="Frequency (in days)"
                    type="text"
                    inputmode="numeric"
                    :disabled="componentLoading"
                    v-on="on"
                  />
                </template>
              </DpplFormField>
            </div>
            <RevertRow
              v-if="!isFieldDefault('flsa_survey_cycle_sla')"
              :disabled="componentLoading"
              @revert="openRevertModal('flsa_survey_cycle_sla')"
            />
          </div>
        </div>

        <div class="w-100">
          <DpplNewButton
            color="primary"
            variant="flat"
            :disabled="!canSubmitTimelines"
            :loading="componentLoading"
            type="submit"
            @click="handleSubmitTimelines"
          >
            Update
          </DpplNewButton>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { isEmpty, isEqual } from 'lodash-es';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplFormField from '@/components/datapeople/DpplFormField.vue';
import AcknowledgementSettingsRevertModal
  from '@/components/settings/components/AcknowledgementSettingsRevertModal.vue';
import RevertRow from '@/components/settings/components/AcknowledgementSettingsRevertRow.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useFlsaSettings } from '@/stores/settings/flsaSettings';
import { useFlsaSettingsForm } from '@/stores/settings/flsaSettingsForm';
import { useTracking } from '@/stores/tracking/tracking';

const TIMELINES_KEYS = ['flsa_survey_cycle_sla'];

const FIELD_LABELS = {
  flsa_survey_cycle_sla: 'FLSA Survey Cycle SLA',
};

const numericInputFieldKeys = new Set([
  'flsa_survey_cycle_sla',
]);

const appLoadingStore = useAppLoading();
const { componentLoading, pageLoading } = storeToRefs(appLoadingStore);

const flsaSettingsForm = useFlsaSettingsForm();
const {
  values,
  errors,
  errorMessages,
} = storeToRefs(flsaSettingsForm);

const flsaSettingsStore = useFlsaSettings();
const { defaults } = storeToRefs(flsaSettingsStore);
const { load, update } = flsaSettingsStore;

const showRevertModal = ref(false);
const pendingRevertKey = ref(null);
const pendingFieldLabel = ref('');
const originalValuesTimelines = ref(null);

const pendingRevertDefault = computed(() => {
  const key = pendingRevertKey.value;

  if (!key) return '';

  return String(defaults.value?.[key] ?? '');
});

const isFieldDefault = (key) => {
  const current = String(values.value?.[key] ?? '');
  const defaultValue = String(defaults.value?.[key] ?? '');

  return current === defaultValue;
};

const openRevertModal = (key) => {
  pendingRevertKey.value = key;
  pendingFieldLabel.value = FIELD_LABELS[key] ?? key;
  showRevertModal.value = true;
};

const handleRevertModalClose = () => {
  showRevertModal.value = false;
};

const normalizeValues = (vals, keys) => keys.reduce((acc, key) => {
  const raw = vals?.[key];
  acc[key] = raw == null || raw === '' ? '' : String(raw);
  return acc;
}, {});

const captureOriginalValuesTimelines = () => {
  originalValuesTimelines.value = normalizeValues(values.value, TIMELINES_KEYS);
};

const isSectionValid = (keys) => keys.every((key) => !errorMessages.value?.[key]);

const isDirtyTimelines = computed(() => (
  !!originalValuesTimelines.value
  && !isEqual(normalizeValues(values.value, TIMELINES_KEYS), originalValuesTimelines.value)
));

const canSubmitTimelines = computed(() => (
  isSectionValid(TIMELINES_KEYS) && isDirtyTimelines.value && !componentLoading.value
));

const handleSubmitTimelines = async () => {
  const baseline = originalValuesTimelines.value;
  const current = normalizeValues(values.value, TIMELINES_KEYS);

  const changedKeys = baseline
    ? TIMELINES_KEYS.filter((key) => current[key] !== baseline[key])
    : [];

  const success = await update(TIMELINES_KEYS);

  if (success) {
    changedKeys.forEach((key) => {
      useTracking().flsaSettingsUpdated({
        section: 'FLSA Survey Timelines',
        field: FIELD_LABELS[key],
      });
    });

    captureOriginalValuesTimelines();
  }
};

const form = computed(() => ({
  values: values.value,
  errors: errors.value,
  errorMessages: errorMessages.value,
  labels: {},
}));

const handleFormValueUpdate = (payload) => {
  if (isEmpty(payload || {})) return;

  const newValue = { ...payload };

  numericInputFieldKeys.forEach((key) => {
    if (key in newValue) {
      const value = newValue[key];
      const str = value === null || value === undefined ? '' : String(value);

      newValue[key] = str.replace(/\D/g, '');
    }
  });

  flsaSettingsForm.setFormValues(newValue);
};

const revertField = (key) => {
  const defaultValue = defaults.value?.[key] ?? '';

  handleFormValueUpdate({ [key]: defaultValue });
};

const handleRevertConfirm = () => {
  const key = pendingRevertKey.value;

  if (key) {
    revertField(key);

    useTracking().flsaSettingsReverted({
      section: 'FLSA Survey Timelines',
      field: FIELD_LABELS[key] ?? key,
    });
  }

  showRevertModal.value = false;
};

onMounted(async () => {
  await load();
  captureOriginalValuesTimelines();
});
</script>

<style scoped lang="less">
.form-wrapper {
  border-radius: 10px;
  background: @white;
  padding: 40px;
}

.flsa-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
}

.two-col-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
}

.section-desc {
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  color: @text-dark;
}

.section-description {
  max-width: 100%;
  font-size: 16px;
  font-weight: 500 !important;
  line-height: 1.45;
  color: @text-dark;
}

.section-description-link {
  color: @text-brand;
  font-weight: 500 !important;
  text-decoration: underline;

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }
}

.field-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.form-field {
  min-width: 150px;
}

:deep(.input-numeric .v-input) {
  width: 75px;
}

.days-suffix {
  padding-top: 2px;
}

:deep(.input-is-default input) {
  color: @text-lighter !important;
}
</style>
